import axios from "axios";
import config from "../config";
import {
  reportBackendResponse,
  reportBackendUnreachable,
} from "../common/backendStatus";

// const https = require('https');
//
// const agent = new https.Agent({
//     rejectUnauthorized: false,
// });

const instance = axios.create({
  baseURL: config.WS_BASE_URL,
});

// Every call doubles as a health check: the maintenance overlay comes up when the API
// stops answering during a deploy, and clears itself the moment it answers again.
instance.interceptors.response.use(
  (response) => {
    reportBackendResponse(response.status);
    return response;
  },
  (error) => {
    if (error && error.response) {
      reportBackendResponse(error.response.status);
    } else {
      // No response at all — the backend is restarting, refusing connections, or the
      // machine is offline. The overlay's own probe decides when it is back.
      reportBackendUnreachable();
    }
    return Promise.reject(error);
  }
);

instance.interceptors.request.use(async (config) => {
  const token = localStorage.getItem("token");
  const selectedCountry = localStorage.getItem("selectedCountry");
  const tenantId = localStorage.getItem("tenantId");
  config.headers.Authorization = token ? token : "";
  config.headers.Country = selectedCountry ? selectedCountry : "";
  config.headers.ContentType = "application/json";
  config.headers["X-Tenant-Key"] = tenantId ? tenantId : "main";
  return config;
});

// Resolves the short 6-digit login code typed at login to the real tenant_key.
// The tenant_key is what every other call sends as X-Tenant-Key.
export const resolveTenant = async (code) =>
  await instance.get("portal/tenant/resolve/" + code);

export const login = async (username, password) =>
  await instance.post("portal/user/login", { username, password });

export const getStats = async () => await instance.get("portal/user/stats");

export const getFaq = async () => await instance.get("app/faq");

export const addFaqCategory = async (titleEn, titleAr, isActive) =>
  await instance.post("app/faq/category/add", { titleEn, titleAr, isActive });

export const updateFaqCategory = async (id, titleEn, titleAr, isActive) =>
  await instance.post("app/faq/category/update", {
    id,
    titleEn,
    titleAr,
    isActive,
  });

export const deleteFaqCategory = async (id) =>
  await instance.post("app/faq/category/delete", { id });

export const getFaqQuestions = async (id) =>
  await instance.get("app/faq/category/" + id);

export const addFaqQuestion = async (
  questionEn,
  questionAr,
  answerEn,
  answerAr,
  categoryId
) =>
  await instance.post("app/faq/add", {
    questionEn,
    questionAr,
    answerEn,
    answerAr,
    categoryId,
  });

export const updateFaqQuestion = async (
  id,
  questionEn,
  questionAr,
  answerEn,
  answerAr,
  categoryId
) =>
  await instance.post("app/faq/add", {
    id,
    questionEn,
    questionAr,
    answerEn,
    answerAr,
    categoryId,
  });

export const deleteFaqQuestion = async (id) =>
  await instance.post("app/faq/delete", { id });

export const getAllAdminParams = async () => await instance.get("app/params");

export const addAdminParam = async (paramKey, paramName, paramValue) =>
  await instance.post("app/params/add", { paramKey, paramName, paramValue });

export const updateAdminParam = async (id, paramKey, paramName, paramValue) =>
  await instance.post("app/params/add", {
    id,
    paramKey,
    paramName,
    paramValue,
  });

export const deleteAdminParam = async (id) =>
  await instance.post("app/params/delete", { id });

export const getAllReviews = async () => await instance.get("app/order/review");

export const getAllCountries = async () => await instance.get("app/country");

export const addCountry = async (
  nameEn,
  nameAr,
  countryCode,
  currency,
  currencyAr,
  isActive
) =>
  await instance.post("app/country/add", {
    nameEn,
    nameAr,
    countryCode,
    currency,
    currencyAr,
    isActive,
  });

export const updateCountry = async (
  id,
  nameEn,
  nameAr,
  countryCode,
  currency,
  currencyAr,
  isActive
) =>
  await instance.post("app/country/add", {
    id,
    nameEn,
    nameAr,
    countryCode,
    currency,
    currencyAr,
    isActive,
  });

export const getCities = async (id) => await instance.get("app/city/" + id);

export const addCity = async (cityEn, cityAr, isActive, countryId) =>
  await instance.post("app/city/add", { cityEn, cityAr, isActive, countryId });

export const updateCity = async (id, cityEn, cityAr, isActive, countryId) =>
  await instance.post("app/city/add", {
    id,
    cityEn,
    cityAr,
    isActive,
    countryId
  });

export const getNeighbourhoods = async () => await instance.get("app/neighborhood/");

export const addNeighbourhood = async (neighNameEn, neighNameAr, isActive, cityId) =>
  await instance.post("app/neighborhood/add", { neighNameEn, neighNameAr, isActive, cityId });

export const updateNeighbourhood = async (id, neighNameEn, neighNameAr, isActive, cityId) =>
  await instance.post("app/neighborhood/add", {
    id,
    neighNameEn,
    neighNameAr,
    isActive,
    cityId
  });

export const getAllCoupons = async () =>
  await instance.get("/app/order/coupon");

export const addCoupon = async (
  couponType,
  couponCode,
  couponValue,
  startDatetime,
  endDatetime,
  minimumOrder,
  isFirstOrderOnly, subscribedUsersOnly, maxUtilization, maxUtilizationPerUser, restrictedToUserIds, restrictedToBuilding, restrictedToRoad, restrictedToBlock, serviceApplicable, applicableForRenewal, isProOnly, maxDiscountValue, overrideMinimumOrder, overrideDeliveryFee, discountId, externalService
) =>
  await instance.post("/app/order/coupon/add", {
    couponType,
    couponCode,
    couponValue,
    startDatetime,
    endDatetime,
    minimumOrder,
    isFirstOrderOnly, subscribedUsersOnly, maxUtilization, maxUtilizationPerUser, restrictedToUserIds, restrictedToBuilding, restrictedToRoad, restrictedToBlock, serviceApplicable, applicableForRenewal, isProOnly, maxDiscountValue, overrideMinimumOrder, overrideDeliveryFee, discountId, externalService
  });

export const updateCoupon = async (
  id,
  couponType,
  couponCode,
  couponValue,
  startDatetime,
  endDatetime,
  minimumOrder,
  isFirstOrderOnly, subscribedUsersOnly, maxUtilization, maxUtilizationPerUser, restrictedToUserIds, restrictedToBuilding, restrictedToRoad, restrictedToBlock, serviceApplicable, applicableForRenewal, isProOnly, maxDiscountValue, overrideMinimumOrder, overrideDeliveryFee, discountId, externalService
) =>
  await instance.post("/app/order/coupon/add", {
    id,
    couponType,
    couponCode,
    couponValue,
    startDatetime,
    endDatetime,
    minimumOrder,
    isFirstOrderOnly, subscribedUsersOnly, maxUtilization, maxUtilizationPerUser, restrictedToUserIds, restrictedToBuilding, restrictedToRoad, restrictedToBlock, serviceApplicable, applicableForRenewal, isProOnly, maxDiscountValue, overrideMinimumOrder, overrideDeliveryFee, discountId, externalService
  });

export const deleteCoupon = async (id) =>
  await instance.post("/app/order/coupon/delete", { id });

export const getAllAdminUsers = async () => await instance.get("/portal/user");

export const getDashboardAuditLog = async (filters = {}) => {
  const params = new URLSearchParams();
  Object.keys(filters).forEach((key) => {
    if (filters[key] !== "" && filters[key] !== null && filters[key] !== undefined) {
      params.append(key, filters[key]);
    }
  });
  return await instance.get("/portal/user/audit-log?" + params.toString());
};

export const addAdminUser = async (username, password, roles, countryIds, posBranchId) =>
  await instance.post("/portal/user/add", { username, password, roles, countryIds, posBranchId });

export const updateAdminUser = async (id, username, password, roles, countryIds, posBranchId) =>
  await instance.post("/portal/user/add", { id, username, password, roles, countryIds, posBranchId });

export const deleteAdminUser = async (id) =>
  await instance.post("/portal/user/delete", { id });

export const resetAdminUser = async (id) =>
  await instance.post("/portal/user/reset", { id });

export const pushNotification = async (title, body, topic) =>
  await instance.post("/portal/notification/push", { title, body, topic });


export const getNotificationsList = async (startDate, endDate) =>
  await instance.post("portal/notification", { startDate, endDate });


export const getAllDrivers = async () => await instance.get("/app/driver");

export const getAllActiveDrivers = async () => await instance.get("/app/driver/active");

export const addDriver = async (
  fullNameEn,
  fullNameAr,
  mobileNumber,
  isActive
) =>
  await instance.post("/app/driver/add", {
    fullNameEn,
    fullNameAr,
    mobileNumber,
    isActive,
  });

export const updateDriver = async (
  id,
  fullNameEn,
  fullNameAr,
  mobileNumber,
  isActive
) =>
  await instance.post("/app/driver/add", {
    id,
    fullNameEn,
    fullNameAr,
    mobileNumber,
    isActive,
  });

export const assignVehicle = async (
  driver_id,
  vehicle_id
) =>
  await instance.post("/app/driver/assign-vehicle", {
    driver_id,
    vehicle_id
  });

export const assignShift = async (
  driver_id,
  shift_id
) =>
  await instance.post("/app/driver/assign-shift", {
    driver_id,
    shift_id
  });

export const getAssignedVehicle = async () =>
  await instance.get("/app/driver/get-assigned-vehicle");

export const getAssignedShift = async () =>
  await instance.get("/app/driver/get-assigned-shift");

export const getAllShifts = async () => await instance.get("/app/shift");

export const addShift = async (
  shiftName,
  startTime,
  endTime,
  isEnabled
) =>
  await instance.post("/app/shift/add", {
    shiftName,
    startTime,
    endTime,
    isEnabled,
  });


export const enableShift = async (shiftId) =>
  await instance.put(`/app/shift/enable/${shiftId}`);

export const disableShift = async (shiftId) =>
  await instance.put(`/app/shift/disable/${shiftId}`);

export const getAllVechicles = async () => await instance.get("/app/vehicle");

export const addVechicle = async (
  vehicle_number,
  is_enabled
) =>
  await instance.post("/app/vehicle/add", {
    vehicle_number,
    is_enabled
  });

export const enableVechicle = async (vehicleId) =>
  await instance.put(`/app/vehicle/enable/${vehicleId}`);

export const disableVechicle = async (vehicleId) =>
  await instance.put(`/app/vehicle/disable/${vehicleId}`);


export const updateShowToUser = async (orderId, photoId, showToUser) =>
  await instance.post(`/app/order/laundryReqs/action/${orderId}`, { photoId, showToUser });



export const getAllSlots = async () => await instance.get("/app/order/slots");


export const getSlotsForDate = async (date, deliveryType, slotId) => await instance.post("/app/order/slots/admin", { date, deliveryType, slotId });

export const addSlot = async (
  dayId,
  startTime,
  endTime,
  numberOfConcurrentBookings,
  deliveryType,
  driverId,
  laundryId,
  city,
  blocks, restrictedToRoad, restrictedToBuilding, isSubscription, turnAroundTime, proTurnAroundTime
) =>
  await instance.post("/app/order/slots/add", {
    dayId,
    startTime,
    endTime,
    numberOfConcurrentBookings,
    deliveryType,
    driverId,
    laundryId,
    city,
    blocks, restrictedToRoad, restrictedToBuilding, isSubscription, turnAroundTime, proTurnAroundTime
  });

export const updateSlot = async (
  id,
  dayId,
  startTime,
  endTime,
  numberOfConcurrentBookings,
  deliveryType,
  driverId,
  laundryId,
  city,
  blocks, restrictedToRoad, restrictedToBuilding, isSubscription, turnAroundTime, proTurnAroundTime
) =>
  await instance.post("/app/order/slots/add", {
    id,
    dayId,
    startTime,
    endTime,
    numberOfConcurrentBookings,
    deliveryType,
    driverId,
    laundryId,
    city,
    blocks, restrictedToRoad, restrictedToBuilding, isSubscription, turnAroundTime, proTurnAroundTime
  });

export const deleteSlot = async (id) =>
  await instance.post("/app/order/slots/delete", { id });


export const getBookedSlots = async (
  date, driverId
) =>
  await instance.post("/app/order/slots/bookedSlots", {
    date, driverId
  });

export const getAllTransactions = async () =>
  await instance.get("/app/transaction");

export const getAllServices = async () => await instance.get("/app/service");
export const getAllServiceTypes = async () => await instance.get("/app/service-type");
export const getAllActiveServices = async () => await instance.get("/app/service/activeWithSubs");
export const getAllActiveServicesAndPerItem = async () => await instance.get("/app/service/activeWithSubsAndPerItem");
export const getAllActiveServicesAndPerItemLaundry = async () => await instance.get("/app/service/activeWithSubsAndPerItemForLaundry");
export const getActiveServicesWithoutSubs = async () => await instance.get("/app/service/active");

const appendServiceFormData = (
  formData,
  {
    id,
    nameEn,
    nameAr,
    shortDescriptionEn,
    shortDescriptionAr,
    descriptionEn,
    descriptionAr,
    imageUrl,
    isActive,
    price,
    bagPrice,
    detailDescriptionEn,
    detailDescriptionAr,
    colorText,
    colorBackground,
    turnAroundTime,
    mainServiceId,
    defaultLaundry,
    isPerItem,
    isCategoryOnly,
    isShowInService,
    showInApp,
    orderNo,
    servicePricings,
    file,
    file2,
  }
) => {
  const normalizedPrice = price === "" || price == null ? 0 : price;
  const normalizedBagPrice = bagPrice === "" || bagPrice == null ? 0 : bagPrice;
  const normalizedTurnAroundTime =
    turnAroundTime === "" || turnAroundTime == null ? 0 : turnAroundTime;
  const normalizedMainServiceId =
    mainServiceId === "" || mainServiceId == null ? -1 : mainServiceId;
  const normalizedDefaultLaundry =
    defaultLaundry === "" || defaultLaundry == null ? 0 : defaultLaundry;
  const normalizedServicePricings = (servicePricings || []).filter(
    (p) =>
      p != null &&
      p.serviceTypeId !== "" &&
      p.serviceTypeId != null &&
      p.price !== "" &&
      p.price != null
  );

  if (file != null) {
    formData.append("image", file);
  }
  if (file2 != null) {
    formData.append("bagImage", file2);
  }
  if (id != null) {
    formData.append("id", id);
  }

  formData.append("nameEn", nameEn ?? "");
  formData.append("nameAr", nameAr ?? "");
  formData.append("descriptionEn", descriptionEn ?? "");
  formData.append("descriptionAr", descriptionAr ?? "");
  formData.append("imageUrl", imageUrl ?? "");
  formData.append("isActive", isActive ?? 1);
  formData.append("price", normalizedPrice);
  formData.append("bagPrice", normalizedBagPrice);
  formData.append("shortDescriptionEn", shortDescriptionEn ?? "");
  formData.append("shortDescriptionAr", shortDescriptionAr ?? "");
  formData.append("detailDescriptionEn", detailDescriptionEn ?? "");
  formData.append("detailDescriptionAr", detailDescriptionAr ?? "");
  formData.append("colorText", colorText ?? "");
  formData.append("colorBackground", colorBackground ?? "");
  formData.append("turnAroundTime", normalizedTurnAroundTime);
  formData.append("mainServiceId", normalizedMainServiceId);
  formData.append("defaultLaundry", normalizedDefaultLaundry);
  formData.append("isPerItem", isPerItem ?? 0);
  formData.append("isCategoryOnly", isCategoryOnly ?? 0);
  formData.append("isShowInService", isShowInService ?? 1);
  formData.append("showInApp", showInApp ?? 1);
  formData.append("orderNo", orderNo ?? 1);

  normalizedServicePricings.forEach((p, i) => {
    formData.append(`servicePricings[${i}].serviceTypeId`, p.serviceTypeId);
    formData.append(`servicePricings[${i}].price`, p.price);
  });
};

export const addService = async (
  nameEn,
  nameAr,
  shortDescriptionEn, shortDescriptionAr,
  descriptionEn,
  descriptionAr,
  imageUrl,
  bagImageUrl,
  isActive,
  price,
  bagPrice,
  detailDescriptionEn,
  detailDescriptionAr,
  colorText,
  colorBackground,
  turnAroundTime,
  mainServiceId,
  defaultLaundry,
  isPerItem, isCategoryOnly, isShowInService, showInApp, orderNo, servicePricings,
  file,
  file2
) => {
  let formData = new FormData();
  appendServiceFormData(formData, {
    nameEn,
    nameAr,
    shortDescriptionEn,
    shortDescriptionAr,
    descriptionEn,
    descriptionAr,
    imageUrl,
    bagImageUrl,
    isActive,
    price,
    bagPrice,
    detailDescriptionEn,
    detailDescriptionAr,
    colorText,
    colorBackground,
    turnAroundTime,
    mainServiceId,
    defaultLaundry,
    isPerItem,
    isCategoryOnly,
    isShowInService,
    showInApp,
    orderNo,
    servicePricings,
    file,
    file2,
  });

  return await instance.post("/app/service/add", formData);
};
export const updateService = async (
  id,
  nameEn,
  nameAr,
  shortDescriptionEn, shortDescriptionAr,
  descriptionEn,
  descriptionAr,
  imageUrl,
  bagImageUrl,
  isActive,
  price,
  bagPrice,
  detailDescriptionEn,
  detailDescriptionAr,
  colorText,
  colorBackground,
  turnAroundTime,
  mainServiceId,
  defaultLaundry,
  isPerItem, isCategoryOnly, isShowInService, showInApp, orderNo, servicePricings,
  file,
  file2
) => {
  let formData = new FormData();
  appendServiceFormData(formData, {
    id,
    nameEn,
    nameAr,
    shortDescriptionEn,
    shortDescriptionAr,
    descriptionEn,
    descriptionAr,
    imageUrl,
    bagImageUrl,
    isActive,
    price,
    bagPrice,
    detailDescriptionEn,
    detailDescriptionAr,
    colorText,
    colorBackground,
    turnAroundTime,
    mainServiceId,
    defaultLaundry,
    isPerItem,
    isCategoryOnly,
    isShowInService,
    showInApp,
    orderNo,
    servicePricings,
    file,
    file2,
  });
  return await instance.post("/app/service/add", formData);
};

export const getAllUsers = async () => await instance.get("/app/user");

export const updateUser = async (
  id,
  firstName,
  lastName,
  emailAddress,
  birthday,
  gender,
  familyStatus,
  cityId,
  isActive,
  flat,
  building,
  road,
  districtName,
  shortAddress,
  block,
  subscriptionPilot,
  shoesPilot,
  portalNotes, proFreeTrial
) =>
  await instance.post("/app/user/update", {
    id,
    firstName,
    lastName,
    emailAddress,
    birthday,
    gender,
    familyStatus,
    cityId,
    isActive,
    flat,
    building,
    road,
    districtName,
    shortAddress,
    block,
    subscriptionPilot,
    shoesPilot,
    portalNotes, proFreeTrial
  });

export const bulkUpdateUserStatus = async (ids, newStatus) =>
  await instance.post("/app/user/bulkUpdateStatus", { ids, newStatus });


export const bulkUpdateUserPro = async (ids, newStatus) =>
  await instance.post("/app/user/bulkUpdatePro", { ids, newStatus });

export const assignProTrial = async (userId, tenor) => {
  return await instance.post(
    `/app/pro/subscription/proTrial?userId=${userId}`,
    {
      tenor: tenor,
      packageId: 1,
      autoRenew: 0,
      paymentMethodId: 4
    }
  );
};


export const getAllCities = async () => await instance.get("app/city");

export const getMonthlyStats = async () =>
  await instance.get("portal/user/monthlyStats");

// POS (walk-in) equivalents of the app dashboard stats, for the App/POS toggle.
export const getPosStats = async () =>
  await instance.get("portal/user/posStats");
export const getPosMonthlyStats = async () =>
  await instance.get("portal/user/posMonthlyStats");

export const getAllBanners = async () => await instance.get("/app/banner");

export const addBanner = async (
  title,
  subtitle,
  color,
  buttonText,
  titleAr,
  subtitleAr,
  buttonTextAr,
  buttonColor,
  buttonIcon,
  sideImage,
  action,
  coupon,
  noOrdersOnly,
  isProOnly,
  orderNo,
  sideImageFile,
  buttonIconFile
) => {
  let formData = new FormData();
  if (sideImageFile != null) {
    formData.append("sideImageFile", sideImageFile);
  }
  if (buttonIconFile != null) {
    formData.append("buttonIconFile", buttonIconFile);
  }
  formData.append("title", title);
  formData.append("subtitle", subtitle);
  formData.append("color", color);
  formData.append("buttonText", buttonText);
  formData.append("titleAr", titleAr);
  formData.append("subtitleAr", subtitleAr);
  formData.append("buttonTextAr", buttonTextAr);
  formData.append("buttonColor", buttonColor);
  formData.append("buttonIcon", buttonIcon);
  formData.append("sideImage", sideImage);
  formData.append("action", action);
  formData.append("coupon", coupon);
  formData.append("isProOnly", isProOnly);
  formData.append("noOrdersOnly", noOrdersOnly);
  formData.append("orderNo", orderNo);

  return await instance.post("/app/banner/add", formData);
};
export const updateBanner = async (
  id,
  title,
  subtitle,
  color,
  buttonText,
  titleAr,
  subtitleAr,
  buttonTextAr,
  buttonColor,
  buttonIcon,
  sideImage,
  action,
  coupon,
  noOrdersOnly,
  isProOnly,
  orderNo,
  sideImageFile,
  buttonIconFile
) => {
  let formData = new FormData();
  if (sideImageFile != null) {
    formData.append("sideImageFile", sideImageFile);
  }
  if (buttonIconFile != null) {
    formData.append("buttonIconFile", buttonIconFile);
  }
  formData.append("id", id);
  formData.append("title", title);
  formData.append("subtitle", subtitle);
  formData.append("color", color);
  formData.append("buttonText", buttonText);
  formData.append("titleAr", titleAr);
  formData.append("subtitleAr", subtitleAr);
  formData.append("buttonTextAr", buttonTextAr);
  formData.append("buttonColor", buttonColor);
  formData.append("buttonIcon", buttonIcon);
  formData.append("sideImage", sideImage);
  formData.append("action", action);
  formData.append("coupon", coupon);
  formData.append("isProOnly", isProOnly);
  formData.append("noOrdersOnly", noOrdersOnly);
  formData.append("orderNo", orderNo);

  return await instance.post("/app/banner/add", formData);
};


export const deleteBanner = async (id) =>
  await instance.post("/app/banner/delete", { id });


export const getOrders = async (startDate, endDate, option, activeOnly, isSubscription, subscriptionId) =>
  await instance.post("app/order", { startDate, endDate, option, activeOnly, isSubscription, subscriptionId });

export const getUserAddresses = async (id) => await instance.get("app/user/address/" + id);

export const updateUserAddress = async (
  id, name, flat, house, road, districtName,
  shortAddress, block, type, city, instructions, coordinates
) =>
  await instance.post("/app/user/address/update", {
    id, name, flat, house, road, districtName,
    shortAddress, block, type, city, instructions, coordinates
  });

export const deleteUserAddress = async (id) =>
  await instance.post("/app/user/address/adminDelete", { id });

export const bulkAssignDriver = async (ids, newStatus, driverPickup) =>
  await instance.post("/app/order/bulkAssignDriver", { ids, newStatus, driverPickup });

export const bulkUpdateOrderStatus = async (ids, newStatus, refundToWallet, imageFile, qrTags, items, itemTypes) => {
  let formData = new FormData();
  formData.append("ids", ids);
  formData.append("newStatus", newStatus);
  if (refundToWallet != null) {
    formData.append("refundToWallet", refundToWallet);
  }
  if (imageFile != null) {
    for (let i = 0; i < imageFile.length; i++) {
      formData.append('imageFile', imageFile[i]);
    }
  }
  if (qrTags != null) {
    formData.append("qrTags", qrTags);
  }
  if (items != null) {
    formData.append("items", items);
  }
  // Per-item garments: {"serviceId": {"serviceTypeId": qty}} so the backend can
  // reprice each service type from service_pricing and keep the type breakdown.
  if (itemTypes != null) {
    formData.append("itemTypes", itemTypes);
  }
  return await instance.post("/app/order/bulkUpdateStatus", formData);

};

export const getAllStatus = async () => await instance.get("app/order/statusList");

export const getOrderDetails = async (id) => await instance.get("app/order/" + id);

// Internal, record-only bag-contents log for an order (order no, placed time,
// bag name, garments + quantity). Used for the dashboard view + print receipt.
export const getBagContentReceipt = async (id) =>
  await instance.get("app/order/bagContents/" + id);

export const getPendingApprovalOrderRecords = async (id) => await instance.get("app/order/bagUpdate/order/" + id);

export const updateOrderPickupDropoff = async (
  id,
  pickupDate,
  pickupTimeslot,
  pickupFlat,
  pickupHouse,
  pickupRoad,
  pickupBlock,
  pickupDistrictName,
  pickupShortAddress,
  pickupCityId,
  pickupAddressType,
  pickupAddressInstructions,
  pickupInstructions,
  pickupLocationCoordinates,
  dropoffDate,
  dropoffTimeslot,
  dropoffFlat,
  dropoffHouse,
  dropoffRoad,
  dropoffBlock,
  dropoffDistrictName,
  dropoffShortAddress,
  dropoffCityId,
  dropoffAddressType,
  dropoffAddressInstructions,
  dropoffInstructions,
  dropoffLocationCoordinates
) =>
  await instance.post("/app/order/updateAdmin", {
    id,
    pickupDate,
    pickupTimeslot,
    pickupFlat,
    pickupHouse,
    pickupRoad,
    pickupBlock,
    pickupDistrictName,
    pickupShortAddress,
    pickupCityId,
    pickupAddressType,
    pickupAddressInstructions,
    pickupInstructions,
    pickupLocationCoordinates,
    dropoffDate,
    dropoffTimeslot,
    dropoffFlat,
    dropoffHouse,
    dropoffRoad,
    dropoffBlock,
    dropoffDistrictName,
    dropoffShortAddress,
    dropoffCityId,
    dropoffAddressType,
    dropoffAddressInstructions,
    dropoffInstructions,
    dropoffLocationCoordinates
  });

export const updateOrderNotes = async (
  id,
  notes
) =>
  await instance.post("/app/order/updateNotes", {
    id,
    notes
  });

export const markOrderForRewash = async (
  id,
  pickupDate,
  pickupSlot,
  dropOffDate,
  dropOffSlot,
) =>
  await instance.post("/app/order/markForRewash", {
    id,
    pickupDate,
    pickupSlot,
    dropOffDate,
    dropOffSlot
  });


export const getAllLaundries = async () => await instance.get("/app/laundry");


export const addLaundry = async (
  name, phoneNumber, locationCoordinates, address, assignedBlocks, status
) =>
  await instance.post("/app/laundry/add", {
    name, phoneNumber, locationCoordinates, address, assignedBlocks, status
  });

export const updateLaundry = async (
  id,
  name, phoneNumber, locationCoordinates, address, assignedBlocks, status
) =>
  await instance.post("/app/laundry/add", {
    id,
    name, phoneNumber, locationCoordinates, address, assignedBlocks, status
  });

export const getAllActiveLaundries = async () => await instance.get("/app/laundry/active");


export const bulkAssignLaundries = async (ids, newStatus) =>
  await instance.post("/app/order/bulkAssignLaundry", { ids, newStatus });

export const getAllLaundryUsers = async (id) => await instance.get("/portal/user/laundry/" + id);

export const addLaundryUser = async (username, password, roles, laundryId) =>
  await instance.post("/portal/user/add", { username, password, roles, laundryId });

export const updateLaundryUser = async (id, username, password, roles, laundryId) =>
  await instance.post("/portal/user/add", { id, username, password, roles, laundryId });

export const deleteLaundryUser = async (id) =>
  await instance.post("/portal/user/delete", { id });

export const resetLaundryUser = async (id) =>
  await instance.post("/portal/user/reset", { id });



export const getLaundryOrders = async (startDate, endDate, option) =>
  await instance.post("app/order/laundryOrders", { startDate, endDate, option });

export const getLaundryOrderDetails = async (id) => await instance.get("app/order/laundryOrders/" + id);

export const getQrOrderDetails = async (qrTag) => await instance.get("app/order/qr/" + qrTag);



export const laundryTagQrs = async (id, newStatus, serviceQrs, imageFile) => {
  let formData = new FormData();
  // if (imageFile != null) {
  //   formData.append("imageFile", imageFile);
  // }
  formData.append("id", id);
  formData.append("newStatus", newStatus);
  if (serviceQrs != null) {
    formData.append('serviceQrs', JSON.stringify(serviceQrs));
  }
  if (imageFile != null) {
    for (let i = 0; i < imageFile.length; i++) {
      formData.append('imageFile', imageFile[i]);
    }
  }
  return await instance.post("/app/order/laundryTagQrs", formData);
};

// export const laundryTagQrs = async (id, newStatus, serviceQrs) =>
// await instance.post("app/order/laundryTagQrs", { id, newStatus, serviceQrs});


export const getAllLaundrySlots = async (id) => await instance.get("/app/laundry/slots/" + id);


export const addLaundrySlot = async (
  dayId,
  startTime,
  endTime,
  laundryId
) =>
  await instance.post("/app/laundry/slots/add", {
    dayId,
    startTime,
    endTime,
    laundryId
  });

export const updateLaundrySlot = async (
  id,
  dayId,
  startTime,
  endTime,
  laundryId
) =>
  await instance.post("/app/laundry/slots/add", {
    id,
    dayId,
    startTime,
    endTime,
    laundryId,
  });

export const deleteLaundrySlot = async (id) =>
  await instance.post("/app/laundry/slots/delete", { id });


export const getAllProSubscriptionPackages = async () => await instance.get("/app/pro/package");

// Monthly package (LNDRY). Editing and purchases need SUBSCRIPTION_PACKAGES; an order's usage needs ORDERS.
export const getMonthlyPackage = async () => await instance.get("/app/package/portal");

export const updateMonthlyPackage = async (pkg) => await instance.post("/app/package/portal/update", pkg);

export const getMonthlyPackagePurchases = async () => await instance.get("/app/package/portal/purchases");

// data is null when the order did not use a package.
export const getOrderPackageUsage = async (orderId) => await instance.get("/app/package/portal/order/" + orderId);

export const updateProSubscriptionPackage = async (
  id, nameEn, nameAr, priceMonthly, priceAnnually, headerColor, discountPercentage, minimumOrder, maximumOrder, isEnabled, logoUrl, file
) => {
  let formData = new FormData();
  if (file != null) {
    formData.append("logoFile", file);
  }
  formData.append("id", id);
  formData.append("nameEn", nameEn);
  formData.append("nameAr", nameAr);
  formData.append("priceMonthly", priceMonthly);
  formData.append("priceAnnually", priceAnnually);
  formData.append("headerColor", headerColor);
  formData.append("discountPercentage", discountPercentage);
  formData.append("minimumOrder", minimumOrder);
  formData.append("maximumOrder", maximumOrder);
  formData.append("isEnabled", isEnabled);
  formData.append("logoUrl", logoUrl);

  return await instance.post("/app/pro/package/add", formData);
};


export const getProSubscriptionFeatures = async (id) => await instance.get("/app/pro/package/feature/" + id);
export const addProSubscriptionFeature = async (
  nameEn, nameAr, textEn, textAr, proPackageId, orderNo, iconUrl, file
) => {
  let formData = new FormData();
  if (file != null) {
    formData.append("iconFile", file);
  }
  formData.append("nameEn", nameEn);
  formData.append("nameAr", nameAr);
  formData.append("textEn", textEn);
  formData.append("textAr", textAr);
  formData.append("packageId", proPackageId);
  formData.append("orderNo", orderNo);
  formData.append("iconUrl", iconUrl);

  return await instance.post("/app/pro/package/feature/add", formData);
};

export const updateProSubscriptionFeature = async (
  id, nameEn, nameAr, textEn, textAr, proPackageId, orderNo, iconUrl, file
) => {
  let formData = new FormData();
  if (file != null) {
    formData.append("iconFile", file);
  }
  formData.append("id", id);
  formData.append("nameEn", nameEn);
  formData.append("nameAr", nameAr);
  formData.append("textEn", textEn);
  formData.append("textAr", textAr);
  formData.append("packageId", proPackageId);
  formData.append("orderNo", orderNo);
  formData.append("iconUrl", iconUrl);

  return await instance.post("/app/pro/package/feature/add", formData);
};

export const deleteProSubscriptionFeature = async (id) =>
  await instance.post("app/pro/package/feature/delete", { id });


export const getAllSubscriptionPackages = async () => await instance.get("/app/subscription/package");
export const getAllActiveSubscriptionPackages = async () => await instance.get("/app/subscription/package/active");

export const addSubscriptionPackage = async (
  frequency, upToBags, isActive, price, imageUrl, file
) => {
  let formData = new FormData();
  if (file != null) {
    formData.append("image", file);
  }
  formData.append("frequency", frequency);
  formData.append("upToBags", upToBags);
  formData.append("imageUrl", imageUrl);
  formData.append("isActive", isActive);
  formData.append("price", price);

  return await instance.post("/app/subscription/package/add", formData);
};
export const updateSubscriptionPackage = async (
  id, frequency, upToBags, isActive, price, imageUrl, file
) => {
  let formData = new FormData();
  if (file != null) {
    formData.append("image", file);
  }
  formData.append("id", id);
  formData.append("frequency", frequency);
  formData.append("upToBags", upToBags);
  formData.append("imageUrl", imageUrl);
  formData.append("isActive", isActive);
  formData.append("price", price);

  return await instance.post("/app/subscription/package/add", formData);
};

export const getProSubscriptions = async (startDate, endDate, asOfStartDate, asOfDate, activeOnly) =>
  await instance.post("app/pro/subscription", { startDate, endDate, asOfStartDate, asOfDate, activeOnly });

export const getSubscriptions = async (startDate, endDate, activeOnly) =>
  await instance.post("app/subscription", { startDate, endDate, activeOnly });

export const getSubscriptionOrdersReport = async (startDate, endDate) =>
  await instance.post("app/subscription/orderReport", { startDate, endDate });


export const getAllSubscriptionStatus = async () => await instance.get("app/subscription/statusList");


export const getSubscriptionDetails = async (id) => await instance.get("app/subscription/" + id);

export const getSubscriptionPackageFromSubId = async (id) => await instance.get("app/subscription/getPackage/" + id);

export const getAllWalletEntries = async () => await instance.get("/app/user/wallet/allWithRunningBalance");

export const getUserWalletEntries = async (id) => await instance.get("app/user/wallet/" + id);

export const addWalletEntry = async (
  userId,
  description,
  trnType,
  cash,
  nonCash,
) =>
  await instance.post("/app/user/wallet/add", {
    userId,
    description,
    trnType,
    cash,
    nonCash
  });

export const getUserReferralEntries = async (id) => await instance.get("app/user/referral/" + id);


export const getAmenitiesLog = async (startDate, endDate) =>
  await instance.post("app/amenity", { startDate, endDate });


export const getPendingActionOrdersCount = async () => await instance.get("app/order/bagUpdate/pendingCount");


export const getOrderBagUpdatesRecords = async (startDate, endDate, option) =>
  await instance.post("app/order/bagUpdate/list", { startDate, endDate, option });


export const bulkUpdateOrderBagRecordStatus = async (ids, newStatus) =>
  await instance.post("/app/order/bagUpdate/action", { ids, newStatus });

export const getBagWeightReport = async (startDate, endDate) =>
  await instance.post("app/order/report/weight", { startDate, endDate });

export const bulkUpdateOrderSlots = async (ids,
  pickupDate,
  pickupTimeslot,
  dropoffDate,
  dropoffTimeslot) =>
  await instance.post("/app/order/bulkUpdateSlots", {
    ids, pickupDate,
    pickupTimeslot,
    dropoffDate,
    dropoffTimeslot
  });


export const getSlotsUtilization = async (startDate, endDate, driverId) =>
  await instance.post("app/order/slots/slotsUtilizationReport", { startDate, endDate, driverId });

export const getAllComplaints = async (startDate, endDate, activeOnly) => await instance.post("/app/complaint", { startDate, endDate, activeOnly });

export const addComplaint = async (
  category,
  description,
  orderId,
  userId,
  complaintStatus,
  compensationValue,
  comment,
  images
) => {
  const formData = new FormData();

  formData.append("category", category);
  formData.append("description", description);
  formData.append("orderId", orderId);
  formData.append("userId", userId);
  formData.append("complaintStatus", complaintStatus);
  formData.append("compensationValue", compensationValue);
  formData.append("comment", comment);

  if (images != null) {
    console.log("print images", images);
    for (let i = 0; i < images.length; i++) {
      console.log(i);
      formData.append('images', images[i]);
    }
  }

  return await instance.post("/app/complaint/raise", formData);
};

export const updateComplaint = async (
  id,
  images,
  complaintStatus,
  compensationValue,
  comment,
  updatedBy
) => {
  const formData = new FormData();

  formData.append("id", id);
  formData.append("complaintStatus", complaintStatus);
  formData.append("compensationValue", compensationValue);
  formData.append("comment", comment);
  formData.append("updatedBy", updatedBy);

  return await instance.patch("/app/complaint/updateComplaint", formData);
};


export const bulkUpdateWelcomeKitSlots = async (ids, dropoffDate, dropoffTimeslot, cityId, flat, building, road, districtName, shortAddress, block) =>
  await instance.post("/app/order/assign-welcome-kit", { ids, dropoffDate, dropoffTimeslot, cityId, flat, building, road, districtName, shortAddress, block });

export const getAllDiscountType = async () => await instance.get("/app/order/discount");

export const addDiscountType = async (name) => await instance.post("/app/order/discount/add", { name });

export const editDiscountType = async (id, name) => await instance.post("/app/order/discount/add", { id, name });

export const deleteDiscountType = async (id) => await instance.post("/app/order/discount/delete", { id });


export const sortDeliveryOrders = async (
  bookedSlotDataDtoList
) =>
  await instance.post("/app/order/slots/sortOrders", {
    bookedSlotDataDtoList
  });

export const getSortedLaundryOrders = async (date) => await instance.post("/app/order/sortedLaundryOrders", { date });

export const getSortedLaundryOrdersByLaundry = async (date, laundryId) => await instance.post("/app/order/sortedLaundryOrders/" + laundryId, { date });

export const getAllDelayedOrders = async () => await instance.get("/portal/delayedOrders");

export const getAllLaundryOrdersByLaundryIdWithCompensation = async (startDate, endDate, laundryId) => await instance.post("/app/order/laundryOrdersCompensation/" + laundryId, { startDate, endDate });

export const getAllOrdersByDriverId = async (startDate, endDate, driverId, filterType) => await instance.post("/app/order/byDriver/" + driverId, { startDate, endDate, filterType });
